import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { Toaster } from 'sonner';
import '@/App.css';

// Pages
import LandingPage from './pages/LandingPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ResidentDashboard from './pages/resident/ResidentDashboard';
import SecurityDashboard from './pages/security/SecurityDashboard';
import AdminDashboard from './pages/admin/AdminDashboard';
import TenantDashboard from './pages/tenant/TenantDashboard';
import VisitorPortal from './pages/visitor/VisitorPortal';

const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading, logout } = useAuth();

  // Check for inactive/deactivated roles
  const inactiveRoles = ['inactive_tenant', 'inactive_resident', 'inactive_security', 'removed', 'vacating', 'resigned'];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/home" replace />;
  }

  // Handle inactive/deactivated users - log them out and redirect to login with message
  if (inactiveRoles.includes(user.role) || (user.status && inactiveRoles.includes(user.status))) {
    logout();
    return <Navigate to="/login" state={{ deactivatedMessage: 'Your account is no longer active. Please contact the administrator for assistance.' }} replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    return <Navigate to="/home" replace />;
  }

  return children;
};

const AppRoutes = () => {
  const { user, logout } = useAuth();
  
  // Check for inactive/deactivated roles for root redirect
  const inactiveRoles = ['inactive_tenant', 'inactive_resident', 'inactive_security', 'removed', 'vacating', 'resigned'];
  
  // If user exists but has an inactive role, redirect to login
  const getHomeRedirect = () => {
    if (!user) return <LandingPage />;
    if (inactiveRoles.includes(user.role) || (user.status && inactiveRoles.includes(user.status))) {
      logout();
      return <Navigate to="/login" state={{ deactivatedMessage: 'Your account is no longer active.' }} replace />;
    }
    return <Navigate to={`/${user.role}`} replace />;
  };

  return (
    <Routes>
      <Route path="/" element={getHomeRedirect()} />
      <Route path="/home" element={<LandingPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/visitor" element={<VisitorPortal />} />
      
      <Route
        path="/resident/*"
        element={
          <ProtectedRoute allowedRoles={['resident']}>
            <ResidentDashboard />
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/tenant/*"
        element={
          <ProtectedRoute allowedRoles={['tenant']}>
            <TenantDashboard />
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/security/*"
        element={
          <ProtectedRoute allowedRoles={['security']}>
            <SecurityDashboard />
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/admin/*"
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <AdminDashboard />
          </ProtectedRoute>
        }
      />
    </Routes>
  );
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRoutes />
        <Toaster position="top-right" richColors />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;